import { Component, Inject, Input } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';

import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';
import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';
import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';
import { isSame } from '../../../../shared/basic-data-management/has-id.interface';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';
import { safeForkJoin } from '../../../../technical-modules/rxjs-helpers/rxjs-helpers';

import { CreationTarifNegatifAccesOrder } from '../entities/creation-tarif-negatif-acces-order.entity';

import { AccesService } from '../services/acces.service';

import { AntenneService } from '../../../structure/antenne/services/antenne.service';
import { AntenneTLR } from '../../../structure/antenne/entities/antenne-tlr.entity';

import { EntrepriseService } from '../../../entreprises/services/entreprise.service';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';

import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';
import { IntervenantTLR } from '../../../humain/intervenant/entities/intervenant-tlr.entity';

import { JSR310ConversionService } from 'src/main/angular/app/shared/jsr310-localdates/services/jsr310-conversion.service';
import { LocalTime } from '../../../../shared/jsr310-localdates/types/local-time.type';
import { LocalDate } from '../../../../shared/jsr310-localdates/types/local-date.type';

import { ParcService } from '../../../structure/parc/services/parc.service';
import { ParcTLR } from '../../../structure/parc/entities/parc-tlr.entity';

import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { IndicatifTelephone } from '../../../../static-types/entities/indicatif-telephone.entity';
import { ParcTechnologie } from '../../../../static-types/entities/parc-technologie.entity';
import { TypeDocument } from '../../../humain/intervenant/components/intervenant-status.component';

type Antenne = {
	entity: AntenneTLR,
	parcs: ParcTLR[],
}

type Selections = {
	[id: number]: boolean,
}

type IntervenantInput = {
	entreprise: EntrepriseTLR | null,
	intervenant: IntervenantTLR | null,
	modificationContacts: boolean,
	indicatifTelephone: IndicatifTelephone | null,
	telephone: string,
	email: string,
	displayablePdpTypes: TypeDocument[]
}

type RadioOption<T> = {
	value: T,
	label: string,
}

type MapIntervenantEntreprise = {
	[idEntreprise: number]: IntervenantTLR[],
}

// Fonction d'aide utilisation dans les fonction d'enregistrement
let notNull = <T>(value: T | null, nom: string) => {
	if (value === null || value === undefined) {
		alert('Le champ ' + nom + ' ne peut pas être vide.');
		throw new Error('Le champ ' + nom + ' ne peut pas être vide.');
	}
	return value;
}

@Component({
	selector: 'planification-tarif-negatif',
	templateUrl: 'planification-tarif-negatif.html',
	styleUrls: ['planification-tarif-negatif.css'],
})
export class PlanificationTarifNegatifComponent {
	// Gestion globale de la fenetre
	displayState$ = new Subject<boolean>();
	step: number = 1;
	noSelection: boolean = false;
	getEntrepriseNom = (entreprise: EntrepriseTLR | null) => {
		if (!entreprise) {
			return '';
		}
		if (!entreprise.nom) {
			return '';
		}
		return entreprise.nom;
	};
	entreprisesById!: MandatoryMap<number, EntrepriseTLR>;
	technologies!: ParcTechnologie[];
	getIntervenantNom = (intervenant: IntervenantTLR | null) => {
		if (!intervenant || !this.entreprisesById || this.entreprisesById.isEmpty()) {
			return '';
		}

		let elems: (string | null)[] = [];
		elems.push(intervenant.nom);
		elems.push(intervenant.prenom);

		// Nom entreprise
		if (intervenant.entrepriseId) {
			let entreprise = this.entreprisesById.get(intervenant.entrepriseId);
			let nomEntreprise = this.getEntrepriseNom(entreprise);
			if (nomEntreprise) {
				elems.push('(' + nomEntreprise + ')');
			}
		}

		return elems.filter($ => $ != null).join(' ');
	};
	getIndicatifLabel = (it: IndicatifTelephone) => it ? (it.name + ' (' + it.code + ') ' + it.dialCode) : '';
	runningJobId: number | null = null;
	jobCompleted: boolean = false;
	
	// Informations statiques bind sur la vue
	antennes!: Antenne[];
	entreprisesSelectionables: EntrepriseTLR[] = [];
	intervenantsSelectionables: IntervenantTLR[] = [];
	intervenantsSelectionablesByEntreprise: MapIntervenantEntreprise = {};
	indicatifTelephoneOptions: IndicatifTelephone[] = [];
	ouiNonOptions: RadioOption<boolean>[] = [
		{
			value: true,
			label: 'Oui',
		},
		{
			value: false,
			label: 'Non',
		},
	];
	
	// Saisies
	allSelected!: boolean;
	technologySelected!: Selections;
	antennesSelected!: Selections;
	parcsSelected!: Selections;
	intervenantsInputs$ = new BehaviorSubject<IntervenantInput[]>([]);
	heureDebut$ = new BehaviorSubject<LocalTime | null>(null);
	dateDebut$ = new BehaviorSubject<LocalDate | null>(null);
	astreinte$ = new BehaviorSubject<boolean | null>(null);
	
	constructor(
		private dialog: MatDialog,

		@Inject(EventBusService) eventBusService: EventBusService,
		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,

		@Inject(AccesService) private accesService: AccesService,
		@Inject(AntenneService) antenneService: AntenneService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(IntervenantService) private intervenantService: IntervenantService,
		@Inject(ParcService) parcService: ParcService,
		@Inject(StaticTypeService) staticTypeService: StaticTypeService,
	) {
		// Chargement des indicatifs de telephone
		staticTypeService.getIndicatifTelephoneList().subscribe(
			(indicatifs) => this.indicatifTelephoneOptions = indicatifs
		)

		////////////////////////////////////
		// Chargement des donnees de base //
		////////////////////////////////////
		
		let antennes$ = antenneService.getAntenneTLRList();
		
		let parcsByAntenne$ = parcService.getParcTLRMapByAntenne();
		
		let structuralHierarchy$ = combineLatest([antennes$, parcsByAntenne$]).pipe(
			map(
				([antennes, parcsByAntenne]) => {
					let antennesH = antennes.map(
						(antenne) => {
							// Recuperer tous les parcs lies a l'antenne
							let parcs = parcsByAntenne.get(antenne.id);
							if (!parcs) {
								parcs = [];
							}
							
							// Filtrer les parcs par le flag
							parcs = parcs.filter($ => $.tarifNegatif && $.actif);
							
							return {
								entity: antenne,
								parcs: parcs,
							} as Antenne;
						}
					);
					
					antennesH = antennesH.filter($ => $.parcs.length > 0);
					
					return antennesH;
				}
			)
		);
		
		let entreprises$ = entrepriseService.getActiveEntreprisesTLRList().pipe(
			map(
				(entreprises) => entreprises.filter(
					$ => $.nom.toLocaleUpperCase().startsWith("ENGIE GREEN")
				)
			)
		);
		
		let entreprisesById$ = entreprises$.pipe(
			map(
				(entreprises) => {
					let map = new Map<number, EntrepriseTLR>();
					entreprises.forEach(
						$ => map.set($.id, $)
					);
					
					return new MandatoryMap<number, EntrepriseTLR>(map, "Entreprise");
				}
			)
		);
		
		let intervenantsEnreprises$ = entreprises$.pipe(
			mergeMap(
				(entreprises) => safeForkJoin(
					entreprises.map(
						(entreprise) => intervenantService.getActiveIntervenantsTLRByEntreprise(entreprise.id).pipe(
							map(
								(intervenants) => ({
									entrepriseId: entreprise.id,
									intervenants: intervenants,
								})
							)
						)
					)
				)
			),
		);
		
		let intervenantsByEntreprises$ = intervenantsEnreprises$.pipe(
			map(
				(data) => {
					let map: MapIntervenantEntreprise = {};
					data.forEach(
						$ => map[$.entrepriseId] = $.intervenants
					);
					
					return map;
				}
			),
		);
		
		let allIntervenants$ = intervenantsEnreprises$.pipe(
			map(
				(data) => {
					let intervenants = data.map($ => $.intervenants);

					let concatIntervenants: IntervenantTLR[] = [];
					intervenants.forEach(
						$ => concatIntervenants = concatIntervenants.concat($)
					);
					
					concatIntervenants = concatIntervenants.sort(
						(a, b) => {
							let c = a.nom.localeCompare(b.nom);
							if (c !== 0) {
								return c;
							}
							
							if (!a.prenom) {
								return -1;
							}
							if (!b.prenom) {
								return 1;
							}
							
							return a.prenom.localeCompare(b.prenom);
						}
					);
					
					return concatIntervenants;
				}
			),
		);
		
		let technologies$ = staticTypeService.getParcTechnologieList(); 
		
		// Ne retenir que les technologies presentes dans les parcs affiches
		let technologiesPresentes$ = forkJoin({
			technologies: technologies$,
			hierarchy: structuralHierarchy$,
		}).pipe(
			map(
				({
					technologies,
					hierarchy,
				}) => {
					let technologiesUsed = new Map<string, boolean>();
					technologies.forEach($ => technologiesUsed.set($.label, false)); 
					
					hierarchy.forEach(antenne => {
						antenne.parcs.forEach(parc => {
							if (parc.energie) {
								technologiesUsed.set(parc.energie, true);
							}
						})
					});
					
					let filteredTechnologies = technologies.filter($ => technologiesUsed.get($.label));
					
					return filteredTechnologies;
				}
			)
		)
		
		let preloadedData$ = forkJoin({
			hierarchy: structuralHierarchy$,
			enteprises: entreprises$,
			entreprisesById: entreprisesById$,
			intervenantsByEntreprises: intervenantsByEntreprises$,
			allIntervenants: allIntervenants$, 
			technologies: technologiesPresentes$,
		});
		
		/////////////////////////////////////
		// Definition des valeurs derivees //
		/////////////////////////////////////

		// La date complete de debut en version JS est calculee depuis l'heure et la date de debut au format Java
		let dateTimeDebut$ = combineLatest([this.heureDebut$, this.dateDebut$]).pipe(map(
			(data) => {
				let [heureDebut, dateDebut] = data;

				if (heureDebut === null || dateDebut === null)
					return null;

				return conversionService.merge(dateDebut, heureDebut);
			}
		));
		let dateTimeDebutJS$ = dateTimeDebut$.pipe(map(
			(dateTimeDebut) => {
				return conversionService.fromNullableLocalDateTime(dateTimeDebut);
			}
		));

		// L'astreinte est une donne derivee de la date+heure de debut
		dateTimeDebutJS$.subscribe(
			(dateTimeDebutJS) => {
				let dateRef = dateTimeDebutJS ? dateTimeDebutJS : new Date();

				let jourSemaine = dateRef.getDay(); // 0 = Dimanche
				let jourOuvre = jourSemaine >= 1 && jourSemaine <= 5;
				let heure = dateRef.getHours();
				let heureOuvree = heure >= 8 && heure < 18;
				
				this.astreinte$.next(!jourOuvre || !heureOuvree);
			}
		);

		/////////////////////////////////////////////////////////
		// Reception et orquestration des demandes d'affichage //
		/////////////////////////////////////////////////////////		
		
		combineLatest([this.displayCommands$, preloadedData$]).subscribe(
			([_, {
				hierarchy,
				enteprises,
				entreprisesById,
				intervenantsByEntreprises,
				allIntervenants, 
				technologies,
			}]) => {
				// Binding initial de la vue
				this.antennes = hierarchy;
				this.step = 1;
				
				this.allSelected = true;
				this.technologies = technologies;
				this.technologySelected = [];
				technologies.forEach($ => this.technologySelected[$.id] = true);
				this.antennesSelected = [];
				this.parcsSelected = [];
				this.antennes.forEach(
					(antenne) => {
						this.antennesSelected[antenne.entity.id] = true;
						antenne.parcs.forEach(
							(parc) => this.parcsSelected[parc.id] = true
						);
					}
				);
				this.noSelection = false;
				
				this.entreprisesSelectionables = enteprises;
				this.intervenantsSelectionablesByEntreprise = intervenantsByEntreprises;
				this.intervenantsSelectionables = allIntervenants;
				
				this.entreprisesById = entreprisesById;

				this.runningJobId = null;
				this.jobCompleted = false;
				
				// Declenchement de l'affichage				
				this.displayState$.next(true);
			}
		)
		
		this.displayState$.pipe(
			filter(
				(display) => !display
			)
		).subscribe(
			(_display) => {
				this.runningJobId = null;
				this.jobCompleted = false;

				// Signaler l'evenement de creation au bus d'evenement general
				if (this.step === 3 ) {
					eventBusService.emit(Events.AccesListChanged, null);
				}
			}
		)
	}

	@Input() id = 'planificationTarifNegatif';
	
	// Liaison de l'observable servant a commander l'affichage du panneau
	private displayCommands$ = new SubjectRelay<void>();
	@Input() set displayCommands(obs: Observable<void> | undefined) {
		this.displayCommands$.registerSource(obs);
	}

	private resetIntervenantsInputs() {
		this.intervenantsInputs$.next([]);
		this.addIntervenantInput();
	}

	changeAllSelected(selected: boolean) {
		if (selected != this.allSelected) {
			this.allSelected = selected;
			
			// Propagation du changement sur les technologies
			this.technologies.forEach(
				(technology) => this.technologySelected[technology.id] = selected
			);

			// Propagation du changement sur toutes les antennes			
			this.antennes.forEach(
				(antenne) => this.changeAntenneSelected(antenne, selected, true)
			);
			
			// Mettre a jour l'etat du bouton "Suivant"
			this.noSelection = !selected;
		}
	}
	
	changeTechnologySelected(technology: ParcTechnologie, selected: boolean) {
		if (selected != this.technologySelected[technology.id]) {
			this.technologySelected[technology.id] = selected;
			let energy = technology.label;
			let allAntennesSelected = true;
			let numberOfSelectedParcs: number = 0;
			
			// Propagation du changement sur les antennes et parcs			
			this.antennes.forEach(
				(antenne) => {
					let allParcsSelected = true;
					
					antenne.parcs.forEach(
						parc => {
							if (parc.energie === energy) {
								this.parcsSelected[parc.id] = selected;
							}
							
							allParcsSelected = allParcsSelected && this.parcsSelected[parc.id];
							numberOfSelectedParcs = this.parcsSelected[parc.id] ? numberOfSelectedParcs + 1 : numberOfSelectedParcs;
						}
					)
					
					this.antennesSelected[antenne.entity.id] = allParcsSelected;
					allAntennesSelected = allAntennesSelected && allParcsSelected;
				}
			);
			
			this.allSelected = allAntennesSelected;
			
			// Mettre a jour l'etat du bouton "Suivant"
			this.noSelection = numberOfSelectedParcs === 0;
		}
	}

	changeAntenneSelected(antenne: Antenne, selected: boolean, skipNoSelectionUpdate?: boolean) {
		let antenneId = antenne.entity.id;
		if (selected != this.antennesSelected[antenneId]) {
			this.antennesSelected[antenneId] = selected;
			
			// Propagation du changement sur tous les parcs de l'antenne
			antenne.parcs.forEach(
				(parc) => {
					this.changeParcSelected(antenne, parc, selected, skipNoSelectionUpdate);
					
					// Si on coche meme un seul parc alors degriser le bouton "Suivant"
					if (selected && !skipNoSelectionUpdate) {
						this.noSelection = false;
					}
				}
			)
			if (selected) {
				// Si on selectionne l'antenne, cocher aussi "Tous"
				// si toutes les antennes sont finalement cochees
				// apres ce changement
				this.allSelected = this.antennes.every(
					(antenne) => this.antennesSelected[antenne.entity.id]
				);
			} else {
				// Si on a deselectionne l'antenne, alors decocher "Tous"
				this.allSelected = false;
				
				// Verifier aussi s'il reste un parc selectionne pour l'etat
				// du bouton "Suivant"
				if (!skipNoSelectionUpdate) {
					this.noSelection = this.checkNoSelectionLeft();
				}
			}
		}
	}

	changeParcSelected(antenne: Antenne, parc: ParcTLR, selected: boolean, skipNoSelectionUpdate?: boolean) {
		let parcId = parc.id;
		if (selected != this.parcsSelected[parcId]) {
			this.parcsSelected[parcId] = selected;
			
			// Propagation du changement
			if (selected) {
				this.antennesSelected[parc.antenneId] = antenne.parcs.every(
					(parc) => this.parcsSelected[parc.id]
				);
				this.allSelected = this.antennes.every(
					(antenne) => this.antennesSelected[antenne.entity.id]
				);
				
				this.noSelection = false;
			} else {
				this.allSelected = false;
				this.antennesSelected[parc.antenneId] = false;
				
				// Verifier aussi s'il reste un parc selectionne pour l'etat
				// du bouton "Suivant"
				if (!skipNoSelectionUpdate) {
					this.noSelection = this.checkNoSelectionLeft();
				}
			}
		}
	}
	
	// Indique s'il ne reste aucun parc selectionne
	private checkNoSelectionLeft(): boolean {
		for (let antenne of this.antennes) {
			for (let parc of antenne.parcs) {
				if (this.parcsSelected[parc.id]) {
					return false;
				}
			}
		}
		
		return true;
	}

	addIntervenantInput() {
		let inputs = this.intervenantsInputs$.getValue();
		inputs.push({
			entreprise: null,
			intervenant: null,
			modificationContacts: false,
		} as IntervenantInput);
		this.intervenantsInputs$.next(inputs);
	}

	removeIntervenantInput(index: number) {
		let inputs = this.intervenantsInputs$.getValue();
		inputs.splice(index, 1);
		this.intervenantsInputs$.next(inputs);
	}

	onEntrepriseChange(input: IntervenantInput, entreprise: EntrepriseTLR | null) {
		let inputs = this.intervenantsInputs$.getValue();
		if (isSame(input.entreprise, entreprise)) {
			return;
		}

		input.entreprise = entreprise;
		input.intervenant = null;
		this.intervenantsInputs$.next(inputs);
	}

	onIntervenantChange(input: IntervenantInput, intervenant: IntervenantTLR | null) {
		let inputs = this.intervenantsInputs$.getValue();
		if (isSame(input.intervenant, intervenant)) {
			return;
		}

		input.intervenant = intervenant;
		input.modificationContacts = false;
		input.displayablePdpTypes = []; // car intervenant interne et acces distant

		this.updateContactDisplay(input);

		// Si l'utilisateur selectionne un intervenant avant de selectionner son entreprise,
		// remplir automatiquement l'entreprise		
		if (intervenant && !input.entreprise) {
			// NB: l'entrepriseId est forcement presente car on est cense avoir charge cet
			// intervenant via une entreprise
			let entrepriseId = intervenant.entrepriseId as number;
			let entreprise = this.entreprisesById.get(entrepriseId);
			input.entreprise = entreprise;
		}

		this.intervenantsInputs$.next(inputs);
	}

	private updateContactDisplay(input: IntervenantInput) {
		let intervenant = input.intervenant;

		if (intervenant) {
			let indexIT = this.indicatifTelephoneOptions.findIndex($ => $.dialCode === (intervenant as IntervenantTLR).indicatifTelephone);
			input.indicatifTelephone = this.indicatifTelephoneOptions[indexIT];
			input.telephone = this.nonNull(intervenant.telephone);
			input.email = this.nonNull(intervenant.email);
		}
	}

	onCancelModificationContact(input: IntervenantInput) {
		input.modificationContacts = false;
		this.updateContactDisplay(input);
	}

	private nonNull(value: string | null): string {
		return value ? value : '';
	}

	onInputModificationContact(input: IntervenantInput) {
		input.modificationContacts = false;

		if (!input.intervenant) {
			throw new Error("input.intervenant ne devrait pas etre null a ce point");
		}

		let intervenant = input.intervenant;
		let dialCode = input.indicatifTelephone ? input.indicatifTelephone.dialCode : null;

		this.intervenantService.updateContactInfos(
			intervenant.id,
			dialCode,
			input.telephone,
			input.email,
		).subscribe(
			(_) => {
				intervenant.indicatifTelephone = dialCode;
				intervenant.telephone = input.telephone;
				intervenant.email = input.email;
			}
		)
	}
	
	cancel() {
		this.displayState$.next(false);
	}
	
	next() {
		this.step = 2;
			
		this.resetIntervenantsInputs();
		this.heureDebut$.next(null);
		this.dateDebut$.next(null);
	}
	
	previous() {
		this.step = 1;
	}
	
	finish() {
		this.displayState$.next(false);
	}

	planify() {
		let errors: string[] = [];

		let inputs = this.intervenantsInputs$.getValue();
		let entrepriseMissing = inputs.some($ => $.entreprise === null);		
		if (entrepriseMissing) {
			errors.push("Vous devez renseigner l'entreprise pour chaque ligne.");
		}

		let intervenantMissing = inputs.some($ => $.intervenant === null);		
		if (intervenantMissing) {
			errors.push("Vous devez renseigner les intervenants pour chaque ligne.");
		}
		
		let intervenantsId = inputs
			.map($ => $.intervenant)
			.filter($ => $)
			// Du au filtre precedent, la valeur -1 ne devrai jamais sortir
			.map($ => $ === null ? -1 : $.id)
			;
		let intervenantDoublon = false;
		let known = new Map<number, boolean>();
		for (let intervenantId of intervenantsId) {
			if (known.get(intervenantId)) {
				intervenantDoublon = true;
			}
			known.set(intervenantId, true);
		}
		if (intervenantDoublon) {
			errors.push("Vous ne devez pas avoir d'intervenants en double.");
		}

		let heureDebut = this.heureDebut$.getValue();
		if (heureDebut === null) {
			errors.push("Veuillez renseigner l'heure de début.");
		}

		let dateDebut = this.dateDebut$.getValue();
		if (dateDebut === null) {
			errors.push("Veuillez renseigner la date de début.");
		}
		
		if (this.displayValidationErrors(errors)) {
			return;
		}
		
		let parcIds: number[] = [];
		this.antennes.forEach(
			(antenne) => antenne.parcs.forEach(
				(parc) => {
					if (this.parcsSelected[parc.id]) {
						parcIds.push(parc.id);
					}
				}	
			)
		);

		// Integrer les autres valeurs saisies
		let intervenantsIds = inputs.filter($ => $.intervenant).map($ => notNull($.intervenant, "intervenant").id);
		let chargeAccesId = intervenantsIds[0];
		intervenantsIds.splice(0, 1);

		let debut = this.conversionService.merge(dateDebut as LocalDate, heureDebut as LocalTime);
		let astreinte = this.astreinte$.getValue();

		let order = {
			parcIds: parcIds,
			chargeAccesId: chargeAccesId,
			intervenantsIds: intervenantsIds,
			debut: debut,
			astreinte: astreinte,
		} as CreationTarifNegatifAccesOrder;

		this.accesService.launchCreateTarifNegatifAccesJob(order).subscribe(
			(jobId) => {
				this.runningJobId = jobId;
				this.jobCompleted = false;
				this.step = 3;
			},
			(error: HttpErrorResponse) => {
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la création des accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de la création des accès", error);
			}
		);
	}

	// Affiche les messages d'erreur de validation
	private displayValidationErrors(errors: string[]): boolean {
		if (errors.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: 'Enregistrement impossible',
					messages: errors
				}
			});

			return true;
		}

		return false;
	}
}